/**
 * Charge `src` (l'URL BRUTE, jamais une variante CDN redimensionnée via `resolveImageUrl` — voir
 * `MapLibreAdapter.probeImageDimensions` pour ce cas différent, un simple sondage de ratio) et
 * résout ses dimensions réelles (`naturalWidth`/`naturalHeight`) — utilisé par `createTokaMap`
 * (`setShapeImage`, `startDrawingImage`) pour déduire `ShapeImage.width`/`.height` quand le
 * consommateur ne les précise pas.
 *
 * `img.crossOrigin = "anonymous"` — sans ça, ce chargement se fait en mode "no-cors", alors que
 * `detectSvgImageType` (`fetch`, mode "cors" par défaut) charge SIMULTANÉMENT la MÊME `src` (voir
 * `resolveImageDimensions`/`startDrawingImage` dans `createTokaMap.ts`, qui lancent les deux en
 * parallèle via `Promise.all`), et qu'une image classée raster sera ENSUITE rechargée une 3e fois en
 * mode "cors" par MapLibre lui-même (`upsertRasterShapeImage`, `map.addSource`, qui a besoin des
 * octets bruts pour construire sa texture WebGL). Le cache HTTP du navigateur ne gère pas toujours
 * proprement 2 requêtes concurrentes vers la même URL avec des modes CORS différents — observé en
 * pratique comme un échec CORS intermittent sur une image dont le bucket S3 sous-jacent a pourtant
 * bien `Access-Control-Allow-Origin: *` (confirmé indépendamment du navigateur, via `curl`). Forcer
 * le mode "cors" ici aussi élimine cette incohérence : les 3 requêtes concurrentes/successives vers
 * la même URL restent dans le même mode. Contrepartie acceptée : une image SANS CORS configuré (ex.
 * un SVG hébergé sur un domaine qui n'en a jamais eu besoin jusqu'ici, puisque son AFFICHAGE réel —
 * `MapLibreAdapter.upsertShapeImageOverlay`, un simple `<img>` DOM — n'a lui jamais besoin de CORS)
 * perd juste la détection AUTOMATIQUE de ses dimensions ici (l'échec est déjà non bloquant, voir
 * `resolveImageDimensions`), sans jamais affecter son affichage réel.
 */
export function loadImageDimensions(src: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => reject(new Error(`Impossible de charger l'image "${src}"`));
    img.src = src;
  });
}

/**
 * Sonde best-effort le `Content-Type`/les premiers octets de `src` pour détecter un SVG que
 * l'heuristique d'URL (`isSvgImageSrc`) ne peut pas trancher (URL CDN opaque, sans extension ni
 * préfixe `data:image/svg+xml` — voir `imageFormat.ts`) — utilisé par `createTokaMap`
 * (`resolveImageDimensions`, `startDrawingImage`) pour compléter `ShapeImage.svg` en même temps que
 * `width`/`height`. Retourne `undefined` (jamais `false`) dès que la réponse est non concluante ou
 * que la requête échoue (réseau, CORS) : l'appelant ne doit jamais figer une valeur fausse, et
 * retombe alors sur l'heuristique d'URL existante à chaque rendu (`isSvgShapeImage`), comme si rien
 * n'avait été tenté.
 */
export async function detectSvgImageType(src: string): Promise<boolean | undefined> {
  try {
    const response = await fetch(src);
    if (!response.ok) return undefined;
    const contentType = response.headers.get("content-type") ?? "";
    if (contentType.includes("svg")) return true;
    if (contentType.startsWith("image/")) return false;
    const text = await response.text();
    return /^\s*(<\?xml[^>]*>\s*)?(<!--[\s\S]*?-->\s*)*<svg[\s>]/i.test(text.slice(0, 2000));
  } catch {
    return undefined;
  }
}
